/** Display identities come from Core's permission-filtered participant resolver. */
export interface GroupChatSender {
  id: string;
  name: string;
  isLeader: boolean;
  avatarUrl?: string;
}

export interface GroupChatMessage {
  id: string;
  text: string;
  sender: GroupChatSender;
  sentAt: string;
  isOwn: boolean;
}

export interface GroupChatPage {
  messages: GroupChatMessage[];
  cursor: string | null;
  unreadCount: number;
  firstUnreadMessageId: string | null;
}

/**
 * Backend integration port, shared by web and native. Implementations must enforce
 * group membership on the server, including revoking access when a member leaves.
 * A group home metadata tag alone does not grant or restrict Reach thread access.
 */
export interface GroupChatTransport {
  list(groupId: string, cursor?: string): Promise<GroupChatPage>;
  send(groupId: string, text: string): Promise<void>;
  markRead(groupId: string): Promise<void>;
  subscribe(
    groupId: string,
    onChange: () => void,
    onError: (error: Error) => void,
  ): Promise<() => void>;
}

export function mergeGroupChatMessages(pages: readonly GroupChatPage[]): GroupChatMessage[] {
  const messages = new Map<string, GroupChatMessage>();
  // The newest page owns duplicate ids, so an edit does not regress when paging back.
  for (const page of [...pages].reverse()) {
    for (const message of page.messages) messages.set(message.id, message);
  }
  return [...messages.values()].sort(
    (a, b) => a.sentAt.localeCompare(b.sentAt) || a.id.localeCompare(b.id),
  );
}

export function groupChatText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Chat message must contain text');
  return trimmed;
}
